// useJobApplicationsViewModel.ts - COMPLETE FIXED VERSION
import { useEffect, useReducer, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { JobApplicationsApi } from '../api/jobApplicationsApi';
import { JobApplicationsState, initialJobApplicationsState } from '../model/JobApplicationsState';
import { Application } from '../model/JobApplicationsModel';

// Status mapping for simplified UI
const STATUS_MAP: Record<string, string[]> = {
  'applied': ['submitted'],
  'screening': ['in_review', 'shortlisted'],
  'interviewing': ['interview_scheduled', 'interviewed'],
  'offered': ['offered'],
  'hired': ['hired'],
  'rejected': ['rejected', 'declined'],
  'withdrawn': ['withdrawn', 'on_hold'],
};

type Action =
  | { type: 'FETCH_START' }
  | {
    type: 'FETCH_SUCCESS'; payload: {
      applications: Application[];
      total: number;
      has_more: boolean;
      status_summary?: Record<string, number>;
    }
  }
  | { type: 'FETCH_ERROR'; payload: string }
  | { type: 'SET_STATUS_FILTER'; payload: string }
  | { type: 'SET_CURRENT_PAGE'; payload: number }
  | { type: 'OPEN_DETAILS'; payload: Application }
  | { type: 'CLOSE_DETAILS' }
  | { type: 'SHOW_TOAST'; payload: JobApplicationsState['toast'] }
  | { type: 'SET_VIEW_MODE'; payload: 'grid' | 'list' }
  | { type: 'SET_ROWS_PER_PAGE'; payload: number }
  | { type: 'HIDE_TOAST' };

const reducer = (
  state: JobApplicationsState,
  action: Action
): JobApplicationsState => {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, loading: true, error: null };
    case 'FETCH_SUCCESS': {
      const simplifiedSummary: Record<string, number> = {
        'applied': 0, 'screening': 0, 'interviewing': 0, 'offered': 0,
        'hired': 0, 'rejected': 0, 'withdrawn': 0,
      };

      if (action.payload.status_summary) {
        Object.entries(action.payload.status_summary).forEach(([backendStatus, count]) => {
          for (const [simpleStatus, backendStatuses] of Object.entries(STATUS_MAP)) {
            if (backendStatuses.includes(backendStatus)) {
              simplifiedSummary[simpleStatus] = (simplifiedSummary[simpleStatus] || 0) + (count as number);
              break;
            }
          }
        });
      }

      const totalPages = Math.ceil(action.payload.total / state.applicationsPerPage);

      console.log('📊 Job Apps - Pagination Update:', {
        currentPage: state.currentPage,
        totalPages,
        totalApplications: action.payload.total,
        applicationsInPage: action.payload.applications.length,
        hasMore: action.payload.has_more
      });

      return {
        ...state,
        loading: false,
        applications: action.payload.applications,
        filteredApplications: action.payload.applications,
        statusSummary: simplifiedSummary,
        totalApplications: action.payload.total,
        totalPages,
        hasMore: action.payload.has_more,
      };
    }
    case 'FETCH_ERROR':
      return { ...state, loading: false, error: action.payload };
    case 'SET_CURRENT_PAGE':
      return { ...state, currentPage: action.payload };
    case 'SET_STATUS_FILTER':
      return {
        ...state,
        statusFilter: action.payload,
        currentPage: 1,
      };
    case 'OPEN_DETAILS':
      return { ...state, selectedApplication: action.payload, showDetailsModal: true };
    case 'CLOSE_DETAILS':
      return { ...state, selectedApplication: null, showDetailsModal: false };
    case 'SHOW_TOAST':
      return { ...state, toast: action.payload };
    case 'HIDE_TOAST':
      return { ...state, toast: null };
    case 'SET_VIEW_MODE':
      return { ...state, viewMode: action.payload };
    case 'SET_ROWS_PER_PAGE':
      return { ...state, applicationsPerPage: action.payload, currentPage: 1 };
    default:
      return state;
  }
};

export const useJobApplicationsViewModel = () => {
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(reducer, initialJobApplicationsState);
  const isInitialMount = useRef(true);

  const showToast = useCallback((message: string, type: 'success' | 'error') => {
    dispatch({ type: 'SHOW_TOAST', payload: { message, type } });
    setTimeout(() => dispatch({ type: 'HIDE_TOAST' }), 4000);
  }, []);

  // Fetch function - NO useCallback to avoid dependency issues
  const fetchApplications = async (page: number, statusFilter: string, perPage?: number) => {
    try {
      console.log('🚀 Job Apps - Fetching:', { page, statusFilter });
      dispatch({ type: 'FETCH_START' });

      let backendStatus: string | undefined;
      if (statusFilter) {
        const backendStatuses = STATUS_MAP[statusFilter];
        if (backendStatuses && backendStatuses.length > 0) {
          backendStatus = backendStatuses[0];
        }
      }

    const limit = perPage ?? state.applicationsPerPage;
    const offset = (page - 1) * limit;

      const result = await JobApplicationsApi.getApplications({
        status: backendStatus,
        limit: limit,
        offset: offset,
      });

      console.log('✅ Job Apps - Response:', {
        page,
        offset,
        limit: limit,
        applications: result.applications.length,
        total: result.total,
        has_more: result.has_more
      });

      dispatch({
        type: 'FETCH_SUCCESS',
        payload: result
      });
    } catch (err: any) {
      console.error('❌ Job Apps - Fetch error:', err);
      dispatch({ type: 'FETCH_ERROR', payload: err.message });
      showToast('Failed to fetch applications', 'error');
    }
  };

  // setCurrentPage - SIMPLE version like job browsing
  const setCurrentPage = (page: number | ((prev: number) => number)) => {
    let newPage: number;

    if (typeof page === 'function') {
      newPage = page(state.currentPage);
    } else {
      newPage = page;
    }

    // Validate
    if (newPage < 1) newPage = 1;
    if (state.totalPages > 0 && newPage > state.totalPages) {
      newPage = state.totalPages;
    }

    if (newPage === state.currentPage) {
      console.log('⏭️ Job Apps - Same page, skipping');
      return;
    }

    console.log('📄 Job Apps - Changing page:', state.currentPage, '→', newPage);

    // Update state
    dispatch({ type: 'SET_CURRENT_PAGE', payload: newPage });

    // Fetch new page
    fetchApplications(newPage, state.statusFilter);
  };

  const setStatusFilter = (status: string) => {
    console.log('🔽 Job Apps - Setting filter:', status);
    dispatch({ type: 'SET_STATUS_FILTER', payload: status });
  };

  const openDetails = useCallback(async (id: string) => {
    try {
      const application = await JobApplicationsApi.getApplicationById(id);
      dispatch({ type: 'OPEN_DETAILS', payload: application });
      navigate(`/candidate/applications/${id}`);
    } catch {
      showToast('Failed to load application details', 'error');
    }
  }, [navigate, showToast]);

  // Initial fetch
  useEffect(() => {
    console.log('🎯 Job Apps - Initial fetch');
    fetchApplications(1, '');
  }, []);

  // Fetch when filter changes (excluding initial mount)
  useEffect(() => {
    if (!isInitialMount.current) {
      console.log('🔄 Job Apps - Filter changed, fetching page 1');
      fetchApplications(1, state.statusFilter);
    } else {
      isInitialMount.current = false;
    }
  }, [state.statusFilter]);

  const setViewMode = useCallback((mode: 'grid' | 'list') => {
    dispatch({ type: 'SET_VIEW_MODE', payload: mode });
  }, []);

  const setRowsPerPage = (rows: number) => {
    dispatch({ type: 'SET_ROWS_PER_PAGE', payload: rows });
    fetchApplications(1, state.statusFilter, rows);
  };

  return {
    state,
    setStatusFilter,
    setCurrentPage,
    openDetails,
    closeDetails: useCallback(() => dispatch({ type: 'CLOSE_DETAILS' }), []),
    hideToast: useCallback(() => dispatch({ type: 'HIDE_TOAST' }), []),
    refetchApplications: () => fetchApplications(state.currentPage, state.statusFilter),
    setViewMode,
    setRowsPerPage,
  };
};